import { ArrowRight, TrendingUp } from 'lucide-react';
import { RedesignShell } from '@/components/marketing/redesign/RedesignShell';
import { RedesignButton } from '@/components/marketing/redesign/RedesignButton';
import { RdSection } from '@/components/marketing/redesign/RedesignMotion';
import { usePageMeta } from '@/lib/use-page-meta';
import { usePostAuthRedirect } from '@/hooks/usePostAuthRedirect';
import { PRIMARY_SIGNUP_CTA, SCHEDULE_CALL_CTA, VESTA_SALES_CALENDAR } from '@/lib/marketing-cta';
import { VESTA_CONTACT_EMAIL, VESTA_STARTING_PRICE } from '@/brand/vesta-cfo-brand';

const STEPS = [
  {
    step: 'STEP 01',
    title: 'Connect or upload',
    body: 'Sync Mews directly, or drop a CSV of your daily metrics. QuickBooks lights up cash runway and vendor spend.',
    meta: '~3 min · Mews · CSV · QuickBooks',
  },
  {
    step: 'STEP 02',
    title: 'Set your budget',
    body: 'Occupancy target, channel mix, labor budget. Vesta uses these to flag drift — not generic "AI alerts."',
    meta: '~5 min · Budget · Targets · Channels',
  },
  {
    step: 'STEP 03',
    title: 'Read your briefing',
    body: 'Tomorrow at 7am you get a plain-English summary. RevPAR moves, anomalies, and the one thing to look at today.',
    meta: '~2 min · Email · In-app · Owner reports',
  },
] as const;

export default function Landing() {
  usePostAuthRedirect();
  usePageMeta({
    title: 'Vesta CFO — AI-assisted hotel KPI dashboard',
    description:
      'Connect Mews or upload a CSV — Vesta CFO turns RevPAR, ADR, GOPPAR, and channel mix into a morning briefing you can act on.',
    path: '/',
  });

  return (
    <RedesignShell>
      <section className="hero-split" data-hero="split">
        <div className="hero-split-inner">
          <div>
            <span className="hero-eye">
              <span className="pulse" />
              AI financial intelligence for hotels
            </span>
            <h1 className="hero-h">
              Your hotel&apos;s
              <br />
              numbers, <em>explained.</em>
            </h1>
            <p className="hero-sub">
              Connect Mews or upload a CSV — Vesta CFO turns RevPAR, ADR, GOPPAR, and channel mix into a morning briefing
              you can actually act on. No CFO hire, no spreadsheets.
            </p>
            <div className="hero-ctas">
              <RedesignButton variant="primary" size="lg" to="/auth">
                Start free trial
                <ArrowRight size={14} />
              </RedesignButton>
              <RedesignButton variant="outline" size="lg" to="/inside">
                See it in action
              </RedesignButton>
            </div>
            <div className="hero-trust">
              <span className="dot" />
              14-day free trial · No card required · Onboard in 10 minutes
            </div>
          </div>

          <div style={{ position: 'relative' }}>
            <div className="preview-frame">
              <div className="preview-chrome">
                <span className="dot" style={{ background: '#FF5F57' }} />
                <span className="dot" style={{ background: '#FEBC2E' }} />
                <span className="dot" style={{ background: '#28C840' }} />
                <span className="url">vestacfo.app/dashboard</span>
              </div>
              <div className="preview-body">
                <img src="/marketing/analytics-dashboard.png" alt="Vesta CFO dashboard" />
              </div>
            </div>
            <div className="kpi-pop">
              <span className="ic">
                <TrendingUp size={16} strokeWidth={1.8} />
              </span>
              <div>
                <div className="label">Today RevPAR</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span className="val">$184.20</span>
                  <span className="delta">↑ 4.2%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <RdSection tight>
        <div className="rd-container">
          <div className="bloc-head">
            <div>
              <div className="bloc-eye">
                <span className="num">— 01</span>How it works
              </div>
              <h2 className="bloc-h">
                Three steps. <em>Ten minutes.</em>
                <br />
                You&apos;re running.
              </h2>
            </div>
            <p className="bloc-sub" style={{ maxWidth: 380 }}>
              Connect what you have, tell Vesta what to watch, and read your first briefing tomorrow morning.
            </p>
          </div>
          <div className="steps">
            {STEPS.map((step) => (
              <div key={step.step} className="step">
                <div className="step-n">{step.step}</div>
                <div className="step-i">
                  <span style={{ fontSize: 18, fontWeight: 600 }}>{step.step.slice(-1)}</span>
                </div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
                <div className="step-meta">{step.meta}</div>
              </div>
            ))}
          </div>
        </div>
      </RdSection>

      <RdSection tight>
        <div className="rd-container">
          <div className="spotlight">
            <div>
              <div className="spot-eye">— Briefing</div>
              <h2 className="spot-h">
                A morning brief that
                <br />
                reads like a GM wrote it.
              </h2>
              <p className="spot-b">
                Not a chart dump. A short, declarative paragraph that names what changed, why, and what to do about it —
                built from your PMS, budget, comp set, and OTA feed.
              </p>
              <ul className="spot-list">
                <li>Named anomalies with property-specific context</li>
                <li>Channel commission drift before month-end</li>
                <li>Optional GM email at 7am, opt-in per property</li>
                <li>One-tap &quot;ask follow-up&quot; with cited data</li>
              </ul>
            </div>
            <div className="spot-img">
              <div style={{ background: '#FAF8F2', padding: '24px 28px', fontFamily: 'var(--font-sans)' }}>
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    color: '#C8963E',
                  }}
                >
                  Daily AI briefing · 08:14
                </div>
                <h3
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 400,
                    fontSize: 24,
                    color: 'var(--vesta-navy)',
                    margin: '8px 0 14px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  Strong RevPAR, labor over budget Tuesday
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--rd-text-2)', margin: '0 0 12px' }}>
                  <b style={{ color: 'var(--vesta-navy)' }}>RevPAR is up 4.2%</b> week-on-week, driven by a stronger
                  weekday mix (corporate +9 rooms). ADR held at $246, occupancy softened on Sunday (−2.1pp).
                </p>
                <p style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--rd-text-2)', margin: '0 0 12px' }}>
                  <b style={{ color: 'var(--vesta-navy)' }}>Labor came in 11% over budget</b> Tuesday — likely tied to
                  the Friday banquet shift overlap. Revenue still covered it (+$1.4k F&amp;B), so margin held.
                </p>
                <p style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--rd-text-2)', margin: 0 }}>
                  <b style={{ color: 'var(--vesta-navy)' }}>One thing to look at:</b> Booking.com commission is trending
                  toward 21% of OTA revenue. Repricing direct could recover ~$3.1k/mo.
                </p>
                <div style={{ marginTop: 18, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['Cited: daily_metrics', 'payroll', 'otas'].map((tag) => (
                    <span
                      key={tag}
                      style={{
                        fontSize: 11,
                        padding: '5px 10px',
                        borderRadius: 4,
                        background: '#fff',
                        border: '1px solid var(--rd-border)',
                        color: 'var(--vesta-navy-muted)',
                        fontWeight: 500,
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="spotlight rev" style={{ marginTop: 120 }}>
            <div className="spot-img">
              <div style={{ padding: 0, lineHeight: 0 }}>
                <img src="/marketing/analytics-dashboard.png" alt="Anomaly feed" />
              </div>
            </div>
            <div>
              <div className="spot-eye">— Anomalies</div>
              <h2 className="spot-h">
                Catch revenue leakage
                <br />
                before month-end.
              </h2>
              <p className="spot-b">
                Vesta watches your property data continuously — labor, OTA commissions, vendor spend, channel mix — and
                surfaces variances tied to your numbers, not third-party comp benchmarks.
              </p>
              <ul className="spot-list">
                <li>Real-time anomaly detection on PMS + finance data</li>
                <li>Severity-ranked: green / amber / red</li>
                <li>Linked to the underlying transactions</li>
                <li>Snooze, escalate to GM, or push to Slack</li>
              </ul>
            </div>
          </div>
        </div>
      </RdSection>

      <RdSection tight>
        <div className="rd-container">
          <div className="bloc-head">
            <div>
              <div className="bloc-eye">
                <span className="num">— 02</span>Pricing
              </div>
              <h2 className="bloc-h">
                Start with one property.
                <br />
                <em>Scale when you&apos;re ready.</em>
              </h2>
            </div>
          </div>
          <div className="snapshot-card">
            <div className="left">
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: 'var(--rd-text-3)',
                }}
              >
                Starter
              </div>
              <h3>Independent hotels &amp; inns</h3>
              <div className="price">
                <span className="from">From</span>
                <span className="n">{VESTA_STARTING_PRICE}</span>
                <span className="u">/mo · 1 property</span>
              </div>
              <ul>
                <li>Core KPI dashboard (RevPAR, ADR, GOPPAR)</li>
                <li>Daily AI briefing &amp; anomaly alerts</li>
                <li>Mews or CSV · QuickBooks sync</li>
                <li>Budget vs actual · Reports hub</li>
              </ul>
              <div style={{ marginTop: 24, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <RedesignButton variant="primary" to="/auth">
                  Start free trial
                </RedesignButton>
                <RedesignButton variant="ghost" to="/pricing">
                  See all plans →
                </RedesignButton>
              </div>
            </div>
            <div style={{ background: '#FAF8F2' }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: 'var(--rd-text-3)',
                }}
              >
                Growth · Enterprise
              </div>
              <h3>Boutique groups &amp; management cos.</h3>
              <p style={{ fontSize: 14.5, lineHeight: 1.6, color: 'var(--rd-text-2)', margin: '14px 0 18px' }}>
                Multi-property rollups, Mews sync, team seats, forecasting, owner reports, and custom integrations. Scoped
                on a 30-minute call.
              </p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 10,
                  fontSize: 13,
                  color: 'var(--vesta-navy)',
                }}
              >
                {[
                  'Multi-property dashboard',
                  'Team seats & access',
                  'Weekly owner reports',
                  'SLA & security review',
                ].map((item) => (
                  <div key={item} style={{ display: 'flex', gap: 8 }}>
                    <span style={{ color: '#1FA94D' }}>✓</span>
                    {item}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 24 }}>
                <RedesignButton variant="outline" href={VESTA_SALES_CALENDAR} external>
                  Schedule a call →
                </RedesignButton>
              </div>
            </div>
          </div>
        </div>
      </RdSection>

      <RdSection tight>
        <div className="rd-container">
          <div className="cta-block">
            <div className="h">
              Stop flying blind.
              <br />
              <em>Start this week.</em>
            </div>
            <p>
              Connect your PMS or upload a CSV — Vesta CFO gives you your first dashboard in minutes. No CFO hire, no
              long setup, no spreadsheets.
            </p>
            <div className="row">
              <RedesignButton variant="gold" size="lg" to="/auth">
                {PRIMARY_SIGNUP_CTA}
              </RedesignButton>
              <RedesignButton variant="outline-light" size="lg" href={VESTA_SALES_CALENDAR} external>
                {SCHEDULE_CALL_CTA}
              </RedesignButton>
            </div>
            <div className="fineprint">
              14-day free trial · No credit card required · {VESTA_CONTACT_EMAIL}
            </div>
          </div>
        </div>
      </RdSection>
    </RedesignShell>
  );
}
